import React, {SyntheticEvent, useCallback, useEffect, useMemo, useState} from 'react';
import {TextField, Typography, Autocomplete} from '@mui/material';
import {debounce} from 'lodash-es';
import {LazyQueryHookOptions, useLazyQuery} from '@apollo/client';
import {useParams} from 'react-router-dom';
import Loading from '../../../components/Loading/Loading';
import useAccountGoals from '../../../hooks/useAccountGoals';
import {LIST_3RD_PARTY} from './createTaskQueries';
import GenericDropdown from '../../../components/Common/GenericDropdown';
import {taskRegardingIdField, taskValues} from './tasksFields';
import AutoField, {SelectField} from '../../../components/AutoForm/AutoFieldNew';
import {curoEntity} from '../../../schema-types/curoEntity';
import {TaskRegardingEntityEnum} from '../../../schema-types/globalTypes';
import {useAutoForm} from '../../../components/AutoForm/useAutoForm';
import {ListPots, ListPotsVariables} from '../../../schema-types/ListPots';
import {LIST_POTS} from '../../potQueries';
import {SaltusAny} from '../../../utils/SaltusAny';
import {UPDATES} from '../../../shared/UPDATES';
import validateUUID from '../../../utils/validateUUID';

const MINIMUM_LENGTH = 3;

interface TaskEntityOptionProps {
  accountLoading: boolean;
  contactsList: SaltusAny[];
}

interface ThirdParty {
  name: string;
}

const TaskEntityField = ({accountLoading, contactsList}: TaskEntityOptionProps) => {
  const {values, errors, touched, setFieldValue} = useAutoForm<taskValues>();
  const {
    taskPosts: accountPots,
    accountPotsVariables,
    getTaskPots: getAccountPots,
  } = useGetTaskPots();
  const {goalsLoading, goals} = useAccountGoals();
  // a convenient way to load regardingThirdParty name
  const [localSearch, setLocalSearch] = useState(values.regarding);
  const [get3rdParties, {data: thirdPartyData, loading: thirdPartyLoading}] =
    useLazyQuery<curoEntity>(LIST_3RD_PARTY);

  useEffect(() => {
    switch (values?.selectedEntityType) {
      // case 'Plan':
      case TaskRegardingEntityEnum.PLAN:
        if (accountPots) {
          setFieldValue(
            UPDATES._1301,
            'selectedEntityTypeDetails',
            accountPots?.find((x) => x.id === values?.regarding)?.name,
            false
          );
        }
        break;
      case TaskRegardingEntityEnum.REQUIREMENT:
      // case 'Requirement':
      // fallthrough to 'Planning Objective'
      case TaskRegardingEntityEnum.PLANNING_OBJECTIVE:
        // case 'Planning Objective':
        if (goals) {
          setFieldValue(
            UPDATES._1301,
            'selectedEntityTypeDetails',
            goals?.find((x) => x.id === values?.regarding)?.name,
            false
          );
        }
        break;
      case TaskRegardingEntityEnum.THIRD_PARTY:
        // case 'Third Party':
        if (thirdPartyData?.curoEntity) {
          setFieldValue(
            UPDATES._1301,
            'selectedEntityTypeDetails',
            thirdPartyData?.curoEntity?.find((x: SaltusAny) => x.id === values?.regarding)
              ?.name,
            false
          );
        }
        break;
      default:
        break;
    }
  }, [
    values?.selectedEntityType,
    thirdPartyData?.curoEntity,
    goals,
    accountPots,
    values?.regarding,
  ]);

  useEffect(() => {
    // if (accountPots || values.taskEntityOption !== 'Plan') {
    if (accountPots || values.taskEntityOption !== TaskRegardingEntityEnum.PLAN) {
      return;
    }
    getAccountPots(accountPotsVariables);
  }, [accountPots, values.taskEntityOption]);

  const changeSearch = useCallback(
    debounce((newSearch) => {
      // Return early to prevent an unneeded request
      if (newSearch === '' || newSearch?.length < MINIMUM_LENGTH) {
        return;
      }
      setLocalSearch(newSearch);
      get3rdParties({
        variables: {
          name: newSearch,
        },
      });
    }, 350),
    [setLocalSearch]
  );

  const onChange = useCallback(
    (e: SaltusAny) => {
      changeSearch(e.target.value);
    },
    [changeSearch]
  );

  const taskFieldMemo = useMemo(() => {
    return {
      ...taskRegardingIdField,
      label: 'Select Planning objective / Goal',
      options: goals.map((x) => ({value: x.id, label: x.name || x.id})),
    };
  }, [taskRegardingIdField, goals]);

  const regardingMemo: SelectField = useMemo(() => {
    return {
      ...taskRegardingIdField,
      label: 'Select Plan',
      options: accountPots?.length
        ? accountPots
            ?.filter(
              (x) =>
                !!x.servicingRights &&
                x.servicingRights !== 'LOA required' &&
                x.servicingRights !== 'No information rights' // filtering out shell plans and dummy plans
            )
            .map((x) => ({value: x.id, label: x.name || x.id}))
        : [],
    };
  }, [accountPots]);
  // const z = 1;
  // if (z === 1) {
  //   return <>{values.taskEntityOption}</>;
  // }

  switch (values.taskEntityOption) {
    case TaskRegardingEntityEnum.CONTACT:
      // case 'Contact':
      return (
        <GenericDropdown
          value={values.regarding!}
          onChange={(x) => {
            setFieldValue(UPDATES._1306, 'regarding', x);
          }}
          list={contactsList}
          loading={accountLoading}
          label={values.taskEntityOption}
        />
      );
    case TaskRegardingEntityEnum.PLAN:
      // case 'Plan':
      if (accountPots) {
        return <AutoField key={`${values.regarding}`} field={regardingMemo} />;
      }
      return <Loading />;

    case TaskRegardingEntityEnum.REQUIREMENT:
    // case 'Requirement':
    // fallthrough to 'Planning Objective'

    case TaskRegardingEntityEnum.PLANNING_OBJECTIVE:
      // case 'Planning Objective':
      if (!goalsLoading) {
        return <AutoField field={taskFieldMemo} />;
      }
      return <Loading />;

    case TaskRegardingEntityEnum.THIRD_PARTY:
      return (
        <>
          <Autocomplete
            loading={accountLoading || thirdPartyLoading}
            loadingText={<Loading />}
            autoSelect
            disableClearable
            value={localSearch as any}
            isOptionEqualToValue={(y, value) => y.id === value}
            options={thirdPartyData?.curoEntity || []}
            renderOption={(props, option) => {
              return (
                <li {...props} data-testid={option.name}>
                  {option.name}
                </li>
              );
            }}
            filterOptions={(option) => option}
            getOptionLabel={(option: SaltusAny) => option?.name || option}
            noOptionsText={
              localSearch ? <div>No third parties found for {localSearch}</div> : null
            }
            renderInput={(params) => (
              <>
                <TextField
                  error={!!errors.regarding && touched.regarding}
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...params}
                  label="Search Third Party Contact"
                  InputLabelProps={{
                    htmlFor: params.id,
                  }}
                  margin="normal"
                  value={localSearch}
                  onChange={onChange}
                  onSelect={(e: SyntheticEvent) => {
                    const currentSearch = (e.target as HTMLTextAreaElement).value;
                    if ((thirdPartyData?.curoEntity || []).length < 1) {
                      return;
                    }
                    const thirdParty =
                      thirdPartyData?.curoEntity?.length === 1
                        ? thirdPartyData.curoEntity[0]
                        : thirdPartyData?.curoEntity?.find(
                            (t) => t.name === currentSearch
                          );
                    setFieldValue(UPDATES._1306, 'regarding', thirdParty?.id);
                    setLocalSearch(
                      (previousLocalSearch) => thirdParty?.name ?? previousLocalSearch
                    );
                  }}
                  inputProps={{
                    ...params.inputProps,
                    type: 'search',
                  }}
                />
              </>
            )}
          />
          {/* hacky but works */}
          {!thirdPartyLoading && localSearch && !values.regarding ? (
            <div>No third parties found for {localSearch}</div>
          ) : null}
          {errors.regarding && touched.regarding ? (
            <Typography color="error">Required</Typography>
          ) : null}
        </>
      );
    default:
      return null;
  }
};

export default TaskEntityField;

export const useGetTaskPots = () => {
  const {accountId} = useParams();

  const [_getPotsToContext, {data: contactPotsData, loading}] = useLazyQuery<
    ListPots,
    ListPotsVariables
  >(LIST_POTS);

  // to get the account pots, one should only use these variables below
  const accountPotsVariables: LazyQueryHookOptions<ListPots, ListPotsVariables> = {
    variables: {
      accountId: validateUUID(accountId)!,
      parentOnly: true,
      banksOnly: false,
      curoSync: true,
    },
  };
  const [getTaskPots, {data: accountPotsData, loading: getAccountPotsLoading}] =
    useLazyQuery<ListPots, ListPotsVariables>(LIST_POTS);

  return {
    taskPosts: accountPotsData?.pots,
    getAccountPotsLoading,
    accountPotsVariables,
    getTaskPots,
    // bankAccountProductNameId: bankData?.pots?.[0]?.productName?.id || null, // all products in this query are bank accounts
  };
};
